
import {
  computed,
  ComputedRef,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  PropType,
  ref,
} from 'vue';
import { use } from 'echarts/core';
import type { ComposeOption } from 'echarts/core';
import {
  BarChart, LineChart, PieChart, RadarChart, ScatterChart,
} from 'echarts/charts';
import type {
  BarSeriesOption,
  LineSeriesOption,
  PieSeriesOption,
  RadarSeriesOption,
  ScatterSeriesOption,
} from 'echarts/charts';
import {
  DatasetComponent,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  PolarComponent,
  RadarComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components';
import type {
  DatasetComponentOption,
  DataZoomComponentOption,
  GridComponentOption,
  LegendComponentOption,
  PolarComponentOption,
  RadarComponentOption,
  TitleComponentOption,
  TooltipComponentOption,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import VChart from 'vue-echarts';
import moment from 'moment/moment';
import 'moment/locale/fr';
import { useI18n } from 'vue-i18n';
import { ChartType, DataSet, SpmChart } from '@/types/generated-types/graphql';
import { UserState } from '@/composables/User';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from '@/i18n';
import { store } from '@/store';
import { IntervalDateEnum } from '@/types';
import { getStatsLabelKeyFromSlug } from '@/helpers/statsLabels';
import StatsEmptyState from '@/components/stats/StatsEmptyState.vue';

// Extension du type DataSet pour inclure rawData
interface ExtendedDataSet extends DataSet {
  rawData?: Array<number | null> | null;
}

// Helper pour convertir un dataset en ExtendedDataSet
const toExtendedDataSet = (dataset: DataSet | null): ExtendedDataSet | null => dataset as ExtendedDataSet | null;

// The DOM typings are not exposed by every build setup.
type ResizeEntry = { contentRect: { width: number } };

// Mirrors the fixed .spm-echart height: pie percentages are relative to the canvas.
const CHART_CANVAS_HEIGHT = 400;
// Assumed until the container is measured, and when ResizeObserver is unavailable.
const DEFAULT_CHART_WIDTH = 600;
const PIE_LABEL_LINE_SPACE = 30;
const PIE_LABEL_MIN_WIDTH = 90;
const PIE_LABEL_VALUE_WIDTH = 65;
const PIE_RADIUS_RATIO = 0.28;
const PIE_MIN_RADIUS = 55;

const scalePieRadius = (radius: string | string[], scale: number): string | string[] => {
  if (scale === 1) {
    return radius;
  }

  const scaleOne = (value: string) => `${parseFloat(value) * scale}%`;
  return Array.isArray(radius) ? radius.map(scaleOne) : scaleOne(radius);
};

// Register the minimal ECharts modules required to render the statistics widgets.
use([
  BarChart,
  LineChart,
  PieChart,
  RadarChart,
  ScatterChart,
  DatasetComponent,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  PolarComponent,
  RadarComponent,
  TitleComponent,
  TooltipComponent,
  CanvasRenderer,
]);

export default defineComponent({
  name: 'SpmGraph',
  components: {
    VChart,
    StatsEmptyState,
  },

  props: {
    name: {
      type: String,
      required: true,
    },

    spmChart: {
      type: Object as PropType<SpmChart>,
      required: true,
    },

    startDate: {
      type: Object,
      required: true,
    },

    endDate: {
      type: Object,
      required: true,
    },

    metricId: {
      type: String,
      required: false,
      default: '',
    },

    maxSlices: {
      type: Number,
      required: false,
      default: undefined,
    },
  },

  setup(props, context) {
    const { t } = useI18n();
    const { lang } = UserState.user;
    const locale = lang && SUPPORTED_LOCALES.includes(lang) ? lang : DEFAULT_LOCALE;
    moment.locale(locale);

    const name = computed(() => (props.name ?? '').trim());
    const chart = computed(() => props.spmChart);
    const startDate = computed(() => props.startDate);
    const endDate = computed(() => props.endDate);

    const chartContainer = ref<HTMLElement | null>(null);
    const containerWidth = ref(0);
    let resizeObserver: ResizeObserver | null = null;

    onMounted(() => {
      if (!chartContainer.value || typeof ResizeObserver === 'undefined') {
        return;
      }

      resizeObserver = new ResizeObserver((entries: ResizeEntry[]) => {
        const rect = entries[0]?.contentRect;
        if (rect) {
          containerWidth.value = rect.width;
        }
      });
      resizeObserver.observe(chartContainer.value);
    });

    onBeforeUnmount(() => {
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
    });

    // ECharts clips outside pie labels at the canvas edge instead of shrinking them. So the pie gives
    // way first (down to PIE_MIN_RADIUS), then the label degrades: name + value, value alone, none.
    const pieLabelLayout = computed(() => {
      const width = containerWidth.value || DEFAULT_CHART_WIDTH;
      const base = Math.min(width, CHART_CANVAS_HEIGHT);
      const radiusPx = Math.max(
        PIE_MIN_RADIUS,
        Math.min(base * PIE_RADIUS_RATIO, width / 2 - PIE_LABEL_LINE_SPACE - PIE_LABEL_MIN_WIDTH),
      );
      const availableWidth = width / 2 - radiusPx - PIE_LABEL_LINE_SPACE;

      if (availableWidth < PIE_LABEL_VALUE_WIDTH) {
        return {
          show: false,
          withName: false,
          labelWidth: 0,
          radiusScale: 1,
        };
      }

      return {
        show: true,
        withName: availableWidth >= PIE_LABEL_MIN_WIDTH,
        labelWidth: Math.floor(availableWidth),
        // '70%' means a radius of 0.35 * base.
        radiusScale: radiusPx / (base * 0.35),
      };
    });

    // État pour gérer le zoom dynamique
    const zoomStart = ref(0);
    const zoomEnd = ref(100);
    const isZooming = ref(false);
    let zoomTimeout: ReturnType<typeof setTimeout> | null = null;

    // Fonction pour gérer les événements de zoom
    const handleDataZoom = (params: any) => {
      // Marquer qu'on est en train de zoomer
      isZooming.value = true;

      // Annuler le timeout précédent
      if (zoomTimeout) {
        clearTimeout(zoomTimeout);
      }

      // Le format peut varier selon le type de zoom (molette vs slider)
      if (params && params.batch && params.batch[0]) {
        // Format batch (zoom molette)
        zoomStart.value = params.batch[0].start || 0;
        zoomEnd.value = params.batch[0].end || 100;
      } else if (params && params.start !== undefined && params.end !== undefined) {
        // Format direct (slider)
        zoomStart.value = params.start || 0;
        zoomEnd.value = params.end || 100;
      }

      // Attendre 300ms après le dernier événement de zoom avant de réactiver le tooltip
      zoomTimeout = setTimeout(() => {
        isZooming.value = false;
      }, 300);
    };

    const chartType = computed<ChartType>(() => {
      const datasetType = chart.value.data?.datasets?.[0]?.type;
      return datasetType || chart.value.type || ChartType.PIE;
    });

    const isTimeline: ComputedRef<boolean> = computed(() => [ChartType.LINE, ChartType.BAR, ChartType.SCATTER]
      .includes(chartType.value));

    const translateStatLabel = (label: string | null | undefined): string => {
      if (label === null || label === undefined) {
        return '';
      }

      const trimmedLabel = label.trim();
      if (!trimmedLabel) {
        return '';
      }

      const translationKey = getStatsLabelKeyFromSlug(trimmedLabel);
      return translationKey.includes('.') ? t(translationKey) : translationKey;
    };

    const translatedChart = computed<SpmChart>(() => {
      if (!chart.value.data) {
        return chart.value;
      }

      const labels = chart.value.data.labels?.map((label) => translateStatLabel(label));
      const datasets = chart.value.data.datasets?.map((dataset) => {
        if (!dataset) {
          return null;
        }

        const translatedDataset: DataSet = {
          ...dataset,
        };

        if (dataset.label) {
          translatedDataset.label = translateStatLabel(dataset.label);
        }

        return translatedDataset;
      }) ?? null;

      return {
        ...chart.value,
        data: {
          ...chart.value.data,
          labels: labels ?? chart.value.data.labels,
          datasets: datasets ?? chart.value.data.datasets,
        },
      };
    });

    const hasData = computed(() => {
      if (!translatedChart.value.data?.datasets?.length) {
        return false;
      }

      return translatedChart.value.data.datasets.some((dataset) => (dataset?.data || []).some((value) => value !== null && value !== undefined));
    });

    const shouldTranslateTooltip = computed(() => Boolean(props.spmChart.options
      && 'removeValueInTooltip' in props.spmChart.options));

    const translateTooltipLabel = (label: string | undefined): string => {
      if (!label) {
        return '';
      }

      const parts = label.split(' : ');
      parts[0] = translateStatLabel(parts[0]);
      return parts.join(' : ');
    };

    const timelineTooltipFormatter = (params: any) => {
      const items = Array.isArray(params) ? params : [params];
      if (!items.length) {
        return '';
      }

      const axisLabel = items[0].axisValueLabel || items[0].name;
      const header = shouldTranslateTooltip.value ? translateTooltipLabel(axisLabel) : axisLabel;

      const rows = items.map((item: any) => {
        const value = Array.isArray(item.value) ? item.value[item.value.length - 1] : item.value;
        return `${item.marker}${translateStatLabel(item.seriesName)}: ${value}`;
      });

      return [header, ...rows].join('<br/>');
    };

    const repartitionTooltipFormatter = (params: any) => {
      const name = shouldTranslateTooltip.value ? translateTooltipLabel(params.name) : params.name;
      // Utiliser formattedValue si disponible (pour affichage formaté), sinon utiliser value
      const displayValue = params.data?.formattedValue ?? (Array.isArray(params.value) ? params.value[params.value.length - 1] : params.value);
      return `${params.marker}${name}: ${displayValue}`;
    };

    const hasTimeScale = computed<boolean>(() => {
      if (!isTimeline.value) {
        return false;
      }

      const scales = props.spmChart.options?.scales ?? {};
      const explicitTimeScale = (scales as any)?.x?.type === 'time'
        || Array.isArray((scales as any)?.xAxes) && (scales as any).xAxes[0]?.type === 'time';
      if (explicitTimeScale) {
        return true;
      }

      const datasets = translatedChart.value.data?.datasets ?? [];
      return datasets.some((dataset) => (dataset?.data || []).some((point) => point && typeof point === 'object' && 'x' in point));
    });

    // Détecter si une métrique nécessite une moyenne plutôt qu'une somme
    // Basé sur l'ID de la métrique (stable et indépendant de la langue)
    const shouldUseAverage = (): boolean => {
      const metricId = props.metricId.toLowerCase();

      // Liste des IDs de métriques qui nécessitent une moyenne
      const averageMetrics = [
        'average-basket', // Panier moyen
        'average-order-value', // Valeur moyenne de commande
        'conversion-rate', // Taux de conversion
        'open-rate', // Taux d'ouverture
        'click-rate', // Taux de clic
        'bounce-rate', // Taux de rebond
        'unsubscribe-rate', // Taux de désinscription
      ];

      return averageMetrics.some((metric) => metricId.includes(metric));
    };

    // Fonction pour agréger les données selon la période
    const aggregateDataByPeriod = (data: any[], labels: (string | null)[], granularity: 'day' | 'week' | 'month' | 'quarter'): { data: any[]; labels: (string | null)[] } => {
      if (granularity === 'day' || !labels.length) {
        return { data, labels };
      }

      const aggregatedData: { [key: string]: { [datasetIndex: number]: number } } = {};
      const aggregatedCounts: { [key: string]: { [datasetIndex: number]: number } } = {}; // Pour calculer les moyennes
      const aggregatedLabels: string[] = [];
      const labelIndexMap: { [key: string]: number } = {};

      // Première passe : créer les clés de période et mapper les index
      labels.forEach((label, index) => {
        if (!label) return;

        const date = moment(label);
        let periodKey: string;

        switch (granularity) {
          case 'week':
            periodKey = date.startOf('week').format('YYYY-MM-DD');
            break;
          case 'month':
            periodKey = date.startOf('month').format('YYYY-MM');
            break;
          case 'quarter':
            periodKey = `${date.year()}-Q${date.quarter()}`;
            break;
          default:
            periodKey = label;
        }

        if (!aggregatedData[periodKey]) {
          aggregatedData[periodKey] = {};
          aggregatedCounts[periodKey] = {};
          labelIndexMap[periodKey] = aggregatedLabels.length;
          aggregatedLabels.push(periodKey);
        }

        // Agréger les valeurs pour chaque dataset
        data.forEach((dataset: any, datasetIndex: number) => {
          if (!aggregatedData[periodKey][datasetIndex]) {
            aggregatedData[periodKey][datasetIndex] = 0;
            aggregatedCounts[periodKey][datasetIndex] = 0;
          }
          // Préférer rawData (nombres bruts) à data (strings formatées avec séparateurs de milliers)
          const rawDataArray = (dataset as ExtendedDataSet)?.rawData;
          const rawValue = Array.isArray(rawDataArray) ? rawDataArray[index]
            : (Array.isArray(dataset.data) ? dataset.data[index] : 0);
          const value = typeof rawValue === 'number' ? rawValue : (typeof rawValue === 'string' ? parseFloat(rawValue) : 0);

          if (value !== 0) { // Ne compter que les valeurs non nulles
            aggregatedData[periodKey][datasetIndex] += value;
            aggregatedCounts[periodKey][datasetIndex] += 1;
          }
        });
      });

      // Créer les nouveaux datasets avec les données agrégées
      const useAverage = shouldUseAverage(); // Vérifier une seule fois pour toute la métrique
      const newData = data.map((dataset: any, datasetIndex: number) => {
        const aggregatedValues = aggregatedLabels.map((label) => {
          const sum = aggregatedData[label][datasetIndex] || 0;
          const count = aggregatedCounts[label][datasetIndex] || 0;

          // Si c'est une moyenne et qu'on a des valeurs, calculer la moyenne
          if (useAverage && count > 0) {
            return sum / count;
          }
          // Sinon retourner la somme
          return sum;
        });

        return {
          ...dataset,
          data: aggregatedValues,
          rawData: aggregatedValues, // Écraser rawData pour que buildTimelineSeries utilise les valeurs agrégées
        };
      });

      return { data: newData, labels: aggregatedLabels };
    };

    // Déterminer la granularité en fonction du nombre de points visibles
    const determineGranularityFromVisiblePoints = (totalPoints: number, visiblePercentage: number): 'day' | 'week' | 'month' | 'quarter' => {
      const visiblePoints = Math.ceil(totalPoints * (visiblePercentage / 100));

      // Adapter la granularité selon le nombre de points visibles
      if (visiblePoints <= 31) {
        return 'day'; // Moins de 31 points : affichage par jour
      } if (visiblePoints <= 90) {
        return 'week'; // 31-90 points : affichage par semaine
      } if (visiblePoints <= 180) {
        return 'month'; // 90-180 points : affichage par mois
      }
      return 'quarter'; // Plus de 180 points : affichage par trimestre
    };

    // Déterminer la granularité en fonction de la période totale
    const determineGranularity = (startDateVal: any, endDateVal: any): 'day' | 'week' | 'month' | 'quarter' => {
      const start = moment(startDateVal);
      const end = moment(endDateVal);
      const daysDiff = end.diff(start, 'days');

      if (daysDiff <= 31) {
        return 'day'; // Moins d'un mois : affichage par jour
      } if (daysDiff <= 90) {
        return 'week'; // 1-3 mois : affichage par semaine
      } if (daysDiff <= 180) {
        return 'month'; // 3-6 mois : affichage par mois
      }
      return 'quarter'; // Plus de 6 mois : affichage par trimestre
    };

    type TimelineSeriesOption = LineSeriesOption | BarSeriesOption | ScatterSeriesOption;

    const buildTimelineSeries = (
      seriesType: 'line' | 'bar' | 'scatter',
      labels: (string | null | undefined)[],
      useTimeAxis: boolean,
      datasetsToUse?: any[],
      backendOptions?: Record<string, any>,
    ): TimelineSeriesOption[] => (datasetsToUse ?? translatedChart.value.data?.datasets ?? [])
      .filter((dataset): dataset is DataSet => Boolean(dataset))
      .map((dataset, datasetIndex) => {
        const datasetLabel = dataset.label?.trim();
        const datasetName = datasetLabel && datasetLabel.length > 0
          ? datasetLabel
          : `Serie ${datasetIndex + 1}`;

        // Utiliser rawData si disponible (nombres), sinon data (peut contenir des strings formatés)
        const extendedDataset = toExtendedDataSet(dataset);
        const datasetData = extendedDataset?.rawData ?? dataset.data ?? [];

        const seriesData = datasetData.map((point, index) => {
          if (point && typeof point === 'object' && 'x' in point && 'y' in point) {
            const typedPoint: any = point;
            if (useTimeAxis) {
              return [typedPoint.x, typedPoint.y];
            }

            if (seriesType === 'scatter') {
              return [index, Number(typedPoint.y ?? 0)];
            }

            return Number(typedPoint.y ?? 0);
          }

          const numericValue = typeof point === 'number' ? point : Number(point ?? 0);
          if (useTimeAxis) {
            const xValue = labels[index] ?? index;
            return [xValue, numericValue];
          }

          if (seriesType === 'scatter') {
            return [index, numericValue];
          }

          return numericValue;
        });

        const backgroundColor = Array.isArray(dataset.backgroundColor)
          ? dataset.backgroundColor?.[0]
          : dataset.backgroundColor;

        if (seriesType === 'line') {
          return {
            name: datasetName,
            type: 'line',
            smooth: chartType.value === ChartType.LINE,
            showSymbol: chartType.value !== ChartType.BAR,
            areaStyle: dataset.fill ? {} : undefined,
            emphasis: {
              focus: 'series',
            },
            itemStyle: backgroundColor ? { color: backgroundColor } : undefined,
            data: seriesData,
          } as LineSeriesOption;
        }

        if (seriesType === 'bar') {
          // Récupérer barThickness du backend (Chart.js) et l'adapter pour ECharts
          const barThickness = backendOptions?.barThickness;
          let barWidth: number | undefined;

          if (barThickness !== undefined && typeof barThickness === 'number') {
            // Si barThickness est trop petit (< 15), utiliser une valeur raisonnable
            barWidth = barThickness < 15 ? 20 : barThickness;
          }

          return {
            name: datasetName,
            type: 'bar',
            barWidth, // Applique la largeur des barres
            smooth: false,
            showSymbol: false,
            emphasis: {
              focus: 'series',
            },
            itemStyle: backgroundColor ? { color: backgroundColor } : undefined,
            data: seriesData,
          } as BarSeriesOption;
        }

        return {
          name: datasetName,
          type: 'scatter',
          emphasis: {
            focus: 'series',
          },
          itemStyle: backgroundColor ? { color: backgroundColor } : undefined,
          data: seriesData,
        } as ScatterSeriesOption;
      });

    // Slices actually drawn. Shared with the legend, which carries the values when the pie cannot.
    const pieData = computed(() => {
      const labels = translatedChart.value.data?.labels ?? [];
      const dataset = (translatedChart.value.data?.datasets?.[0] ?? null) as ExtendedDataSet | null;

      // Créer un tableau avec toutes les données
      const allData = labels.map((label, index) => {
        // Utiliser rawData pour les valeurs numériques du graphique, data pour l'affichage
        const itemValue = dataset?.rawData?.[index] ?? Number(dataset?.data?.[index] ?? 0);
        const formattedValue = dataset?.data?.[index];
        const backgroundColor = Array.isArray(dataset?.backgroundColor)
          ? dataset?.backgroundColor?.[index]
          : dataset?.backgroundColor;

        return {
          name: label ?? '',
          value: itemValue,
          formattedValue,
          backgroundColor,
          index,
        };
      }).filter((item) => item.value > 0); // Filtrer les valeurs nulles

      // Trier par valeur décroissante
      allData.sort((a, b) => b.value - a.value);

      let data: Array<Record<string, any>>;

      // Si maxSlices est défini et qu'il y a plus de tranches, regrouper les plus petites dans "Autres"
      const maxSlicesValue = props.maxSlices;
      if (maxSlicesValue !== undefined && maxSlicesValue > 0 && allData.length > maxSlicesValue) {
        const topItems = allData.slice(0, maxSlicesValue - 1);
        const otherItems = allData.slice(maxSlicesValue - 1);
        const otherTotal = otherItems.reduce((sum, item) => sum + item.value, 0);

        data = [
          ...topItems.map((item) => ({
            name: item.name,
            value: item.value,
            formattedValue: item.formattedValue,
            itemStyle: item.backgroundColor ? { color: item.backgroundColor } : undefined,
          })),
          {
            name: t('stats.others'),
            value: otherTotal,
            itemStyle: { color: '#94a3b8' }, // Couleur grise neutre
            tooltip: {
              formatter: () => {
                // Afficher seulement les 5 premiers éléments pour éviter une tooltip trop longue
                const topOthers = otherItems.slice(0, 5);
                const remainingCount = otherItems.length - topOthers.length;

                const itemsList = topOthers
                  .map((item) => `${item.name}: ${item.formattedValue ?? item.value}`)
                  .join('<br/>');

                const remainingText = remainingCount > 0
                  ? `<br/><em>... et ${remainingCount} autre${remainingCount > 1 ? 's' : ''}</em>`
                  : '';

                return `<strong>${t('stats.others')}</strong><br/>${t('stats.othersTooltip', { count: otherItems.length })}<br/><strong>Total: ${otherTotal}</strong><br/><br/>${itemsList}${remainingText}`;
              },
            },
          },
        ];
      } else {
        data = allData.map((item) => ({
          name: item.name,
          value: item.value,
          formattedValue: item.formattedValue,
          itemStyle: item.backgroundColor ? { color: item.backgroundColor } : undefined,
        }));
      }

      return data;
    });

    const buildPieSeries = (radius: string | string[], extraOptions: Record<string, unknown> = {}): PieSeriesOption[] => {
      const datasetLabel = translatedChart.value.data?.datasets?.[0]?.label?.trim() ?? '';
      const datasetName = datasetLabel || 'Serie 1';
      const layout = pieLabelLayout.value;

      // Utiliser formattedValue si disponible, sinon utiliser la valeur brute
      const displayValue = (params: any) => params.data?.formattedValue ?? params.value;

      const label = {
        show: layout.show,
        position: 'outside' as const,
        width: layout.labelWidth,
        overflow: 'truncate' as const,
        ellipsis: '…',
        // Value on its own line: truncation eats the end of a line, never the figure.
        formatter: (params: any) => (layout.withName
          ? `${params.name}\n${displayValue(params)}`
          : `${displayValue(params)}`),
      };

      return [
        {
          name: datasetName,
          type: 'pie',
          radius: scalePieRadius(radius, layout.radiusScale),
          avoidLabelOverlap: layout.show,
          label,
          labelLine: { show: layout.show },
          labelLayout: { hideOverlap: true },

          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },

          ...extraOptions,
          data: pieData.value,
        } as PieSeriesOption,
      ];
    };

    const parseNumericValue = (value: any) => {
      if (typeof value === 'number') {
        return value;
      }

      if (typeof value === 'string') {
        // Remove spaces and other formatting characters that may prevent numeric parsing (e.g., 53 790)
        const normalized = value.replace(/[\s\u202f\u00a0]/g, '');
        return Number(normalized);
      }

      return Number(value ?? 0);
    };

    const buildRadarIndicators = () => {
      const labels = translatedChart.value.data?.labels ?? [];

      const globalMaximum = (translatedChart.value.data?.datasets ?? [])
        .map((dataset) => toExtendedDataSet(dataset)?.rawData ?? dataset?.data ?? [])
        .reduce((overallMax, datasetData) => {
          const datasetMax = (datasetData as any[])?.reduce((max, value) => {
            const numericValue = parseNumericValue(value);
            return Math.max(max, numericValue);
          }, 0) ?? 0;

          return Math.max(overallMax, datasetMax);
        }, 0);

      const indicatorMax = globalMaximum > 0 ? globalMaximum * 1.2 : 1;

      return labels.map((label) => ({
        name: label ?? '',
        max: indicatorMax,
      }));
    };

    const buildRadarSeries = (): RadarSeriesOption[] => (translatedChart.value.data?.datasets ?? [])
      .filter((dataset): dataset is DataSet => Boolean(dataset))
      .map((dataset, datasetIndex) => {
        const backgroundColor = Array.isArray(dataset.backgroundColor)
          ? dataset.backgroundColor?.[0]
          : dataset.backgroundColor;

        const datasetLabel = dataset.label?.trim();
        const datasetName = datasetLabel && datasetLabel.length > 0
          ? datasetLabel
          : `Serie ${datasetIndex + 1}`;

        const extendedDataset = toExtendedDataSet(dataset);
        const datasetData = extendedDataset?.rawData ?? dataset.data ?? [];

        return {
          name: datasetName,
          type: 'radar',
          areaStyle: dataset.fill ? {} : undefined,
          lineStyle: backgroundColor ? { color: backgroundColor } : undefined,
          itemStyle: backgroundColor ? { color: backgroundColor } : undefined,
          data: [
            {
              value: datasetData.map((item) => parseNumericValue(item)),
              name: datasetName,
            },
          ],
        } as RadarSeriesOption;
      });

    type ECOption = ComposeOption<
      | BarSeriesOption
      | LineSeriesOption
      | PieSeriesOption
      | RadarSeriesOption
      | ScatterSeriesOption
      | DatasetComponentOption
      | DataZoomComponentOption
      | GridComponentOption
      | LegendComponentOption
      | PolarComponentOption
      | RadarComponentOption
      | TitleComponentOption
      | TooltipComponentOption
    >;

    const chartOptions = computed<ECOption>(() => {
      if (!translatedChart.value.data) {
        return {} as ECOption;
      }

      const labels = translatedChart.value.data.labels ?? [];
      const datasets = translatedChart.value.data.datasets ?? [];

      // Détecter si le backend demande un graphique horizontal (indexAxis: 'y' de Chart.js)
      const backendOptions = translatedChart.value.options ?? {};
      const isHorizontal = backendOptions.indexAxis === 'y';

      // Légende avec espace pour le slider uniquement pour les graphiques timeline
      const legendWithSlider = {
        show: true,
        bottom: 50, // Espace pour le DataZoom slider (30px) + marge (20px)
        type: 'scroll' as const,
      };

      // Légende normale pour les autres graphiques
      const legendNormal = {
        show: true,
        bottom: 0, // En bas sans espace supplémentaire
        type: 'scroll' as const,
      };

      // Long names must wrap: `confine` only moves the box around, it never shrinks it.
      const repartitionTooltip = () => ({
        trigger: 'item' as const,
        formatter: repartitionTooltipFormatter,
        confine: true,
        transitionDuration: 0, // Désactive les animations pour éviter les erreurs pendant le zoom
        extraCssText: `max-width: ${Math.max(180, Math.min(320, containerWidth.value || DEFAULT_CHART_WIDTH) - 32)}px; white-space: normal; word-break: break-word;`,
      });

      // Without pie labels the legend carries the figures: vertical, value first so truncation
      // never eats it.
      const repartitionLegend = () => {
        const valueByName: Record<string, string> = {};
        if (!pieLabelLayout.value.show) {
          pieData.value.forEach((item) => {
            valueByName[item.name] = `${item.formattedValue ?? item.value}`;
          });
        }

        return {
          ...legendNormal,
          orient: (pieLabelLayout.value.show ? 'horizontal' : 'vertical') as 'horizontal' | 'vertical',
          left: 'center',
          pageButtonItemGap: 5,
          pageIconSize: 12,
          tooltip: { show: true },
          formatter: pieLabelLayout.value.show
            ? undefined
            : (itemName: string) => (valueByName[itemName]
              ? `${valueByName[itemName]} — ${itemName}`
              : itemName),
          textStyle: {
            width: Math.max(PIE_LABEL_MIN_WIDTH, Math.min(240, (containerWidth.value || DEFAULT_CHART_WIDTH) - 60)),
            overflow: 'truncate' as const,
            ellipsis: '…',
          },
        };
      };

      if (chartType.value === ChartType.LINE || chartType.value === ChartType.BAR) {
        const useTimeAxis = hasTimeScale.value;

        // Calculer la granularité basée sur le pourcentage visible
        const visiblePercentage = zoomEnd.value - zoomStart.value;
        const granularity = determineGranularityFromVisiblePoints(labels.length, visiblePercentage);

        // Agréger les données selon la granularité calculée
        let processedLabels = labels;
        let processedDatasets = datasets;

        if (granularity !== 'day' && labels.length > 0 && datasets.length > 0) {
          const aggregated = aggregateDataByPeriod(datasets, labels, granularity);
          processedDatasets = aggregated.data;
          processedLabels = aggregated.labels;
        }

        // Pour les graphiques horizontaux (indexAxis: 'y'), inverser les axes
        const axisConfig = isHorizontal ? {
          xAxis: {
            type: 'value' as const,
          },
          yAxis: {
            type: 'category' as const,
            data: processedLabels,
            inverse: true, // Inverser pour avoir le premier élément en haut
          },
        } : {
          xAxis: {
            type: 'category' as const,
            data: processedLabels,
            boundaryGap: chartType.value === ChartType.BAR,
          },
          yAxis: {
            type: 'value' as const,
          },
        };

        return {
          grid: {
            left: '3%',
            right: '4%',
            bottom: 120, // Espace pour légende (50px) + slider (30px) + marges (40px)
            containLabel: true,
          },
          tooltip: {
            show: !isZooming.value, // Désactive le tooltip pendant le zoom
            trigger: 'axis',
            formatter: timelineTooltipFormatter,
            confine: true,
            transitionDuration: 0,
          },
          legend: legendWithSlider,
          ...axisConfig,
          dataZoom: [
            {
              type: 'inside',
              start: zoomStart.value,
              end: zoomEnd.value,
              zoomOnMouseWheel: true,
              moveOnMouseMove: true,
              moveOnMouseWheel: true,
            },
            {
              type: 'slider',
              start: zoomStart.value,
              end: zoomEnd.value,
              height: 30,
              bottom: 10,
              // Design épuré et neutre
              fillerColor: 'rgba(100, 116, 139, 0.12)', // Zone sélectionnée en gris-bleu léger
              borderColor: '#e2e8f0',
              backgroundColor: '#f8fafc',
              dataBackground: {
                lineStyle: {
                  color: '#cbd5e1',
                  width: 1,
                },
                areaStyle: {
                  color: 'rgba(203, 213, 225, 0.2)',
                },
              },
              selectedDataBackground: {
                lineStyle: {
                  color: '#94a3b8',
                  width: 1.5,
                },
                areaStyle: {
                  color: 'rgba(148, 163, 184, 0.25)',
                },
              },
              handleStyle: {
                color: '#fff',
                borderColor: '#94a3b8',
                borderWidth: 2,
                shadowBlur: 3,
                shadowColor: 'rgba(0, 0, 0, 0.1)',
              },
              moveHandleStyle: {
                color: 'rgba(148, 163, 184, 0.4)',
              },
              emphasis: {
                handleStyle: {
                  borderColor: '#64748b',
                  shadowBlur: 5,
                  shadowColor: 'rgba(0, 0, 0, 0.15)',
                },
                moveHandleStyle: {
                  color: 'rgba(148, 163, 184, 0.6)',
                },
              },
            },
          ],
          series: buildTimelineSeries(chartType.value === ChartType.BAR ? 'bar' : 'line', processedLabels, false, processedDatasets, backendOptions),
        };
      }

      if (chartType.value === ChartType.SCATTER) {
        // Calculer la granularité basée sur le pourcentage visible
        const visiblePercentage = zoomEnd.value - zoomStart.value;
        const granularity = determineGranularityFromVisiblePoints(labels.length, visiblePercentage);

        // Agréger les données selon la granularité calculée
        let processedLabels = labels;
        let processedDatasets = datasets;

        if (granularity !== 'day' && labels.length > 0 && datasets.length > 0) {
          const aggregated = aggregateDataByPeriod(datasets, labels, granularity);
          processedDatasets = aggregated.data;
          processedLabels = aggregated.labels;
        }

        return {
          grid: {
            left: '3%',
            right: '4%',
            bottom: 120, // Espace pour légende (50px) + slider (30px) + marges (40px)
            containLabel: true,
          },
          tooltip: {
            show: !isZooming.value, // Désactive le tooltip pendant le zoom
            trigger: 'axis',
            formatter: timelineTooltipFormatter,
            confine: true,
            transitionDuration: 0,
          },
          legend: legendWithSlider,
          xAxis: {
            type: 'category',
            data: processedLabels,
          },
          yAxis: {
            type: 'value',
          },
          dataZoom: [
            {
              type: 'inside',
              start: zoomStart.value,
              end: zoomEnd.value,
              zoomOnMouseWheel: true,
              moveOnMouseMove: true,
              moveOnMouseWheel: true,
            },
            {
              type: 'slider',
              start: zoomStart.value,
              end: zoomEnd.value,
              height: 30,
              bottom: 10,
              // Design épuré et neutre
              fillerColor: 'rgba(100, 116, 139, 0.12)', // Zone sélectionnée en gris-bleu léger
              borderColor: '#e2e8f0',
              backgroundColor: '#f8fafc',
              dataBackground: {
                lineStyle: {
                  color: '#cbd5e1',
                  width: 1,
                },
                areaStyle: {
                  color: 'rgba(203, 213, 225, 0.2)',
                },
              },
              selectedDataBackground: {
                lineStyle: {
                  color: '#94a3b8',
                  width: 1.5,
                },
                areaStyle: {
                  color: 'rgba(148, 163, 184, 0.25)',
                },
              },
              handleStyle: {
                color: '#fff',
                borderColor: '#94a3b8',
                borderWidth: 2,
                shadowBlur: 3,
                shadowColor: 'rgba(0, 0, 0, 0.1)',
              },
              moveHandleStyle: {
                color: 'rgba(148, 163, 184, 0.4)',
              },
              emphasis: {
                handleStyle: {
                  borderColor: '#64748b',
                  shadowBlur: 5,
                  shadowColor: 'rgba(0, 0, 0, 0.15)',
                },
                moveHandleStyle: {
                  color: 'rgba(148, 163, 184, 0.6)',
                },
              },
            },
          ],
          series: buildTimelineSeries('scatter', processedLabels, false, processedDatasets, backendOptions),
        };
      }

      if (chartType.value === ChartType.PIE) {
        return {
          tooltip: repartitionTooltip(),
          legend: repartitionLegend(),
          series: buildPieSeries('70%'),
        };
      }

      if (chartType.value === ChartType.DOUGHNUT) {
        return {
          tooltip: repartitionTooltip(),
          legend: repartitionLegend(),
          series: buildPieSeries(['40%', '70%']),
        };
      }

      if (chartType.value === ChartType.POLARAREA) {
        return {
          tooltip: repartitionTooltip(),
          legend: repartitionLegend(),
          series: buildPieSeries(['15%', '70%'], { roseType: 'area' }),
        };
      }

      if (chartType.value === ChartType.RADAR) {
        return {
          tooltip: {
            trigger: 'item',
            confine: true,
            transitionDuration: 0, // Désactive les animations pour éviter les erreurs pendant le zoom
          },
          legend: legendNormal,
          radar: {
            indicator: buildRadarIndicators(),
          },
          series: buildRadarSeries(),
        };
      }

      return {} as ECOption;
    });

    return {
      t,
      name,
      chartContainer,
      chartType,
      isTimeline,
      hasData,
      chartOptions,
      zoomStart,
      zoomEnd,
      handleDataZoom,
    };
  },
});
